/**
 * Moteur d'evaluation des conditions de reussite d'un exercice HTML/CSS.
 *
 * Fonction pure, sans dependance React : elle recoit le code de l'eleve et la
 * liste des conditions saisies par l'enseignant, et retourne un verdict
 * detaille condition par condition. C'est ce detail qui permet a l'interface
 * d'indiquer *ce qui manque* au lieu d'un echec generique.
 */

import { ConditionKind, ConditionResult, WinEvaluation } from './types';

/** Neutralise les caracteres speciaux d'une chaine utilisee dans une RegExp. */
const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Retire les commentaires avant analyse.
 *
 * Sans cela un eleve validait l'exercice en laissant la reponse attendue en
 * commentaire, sans ecrire le code demande.
 */
const stripCssComments = (css: string): string => css.replace(/\/\*[\s\S]*?\*\//g, ' ');

const stripHtmlComments = (html: string): string => html.replace(/<!--[\s\S]*?-->/g, ' ');

/**
 * Determine la nature d'une condition a partir de sa syntaxe.
 *
 * L'ordre des tests est significatif : `<h1>` doit etre reconnu comme un
 * element avant d'etre teste comme du texte libre, et `h1 { }` comme un
 * selecteur avant d'etre confondu avec une propriete a cause du `:` que
 * pourrait contenir une pseudo-classe.
 */
const classify = (condition: string): ConditionKind => {
  if (condition.startsWith('<') && condition.endsWith('>')) return 'html-element';
  if (condition.includes('{')) return 'css-selector';

  // Un `.` ou `#` en tete designe sans ambiguite une classe ou un identifiant,
  // avec ou sans accolades.
  if (/^[.#][\w-]/.test(condition)) return 'css-selector';

  const separator = condition.indexOf(':');
  if (separator !== -1) {
    // Un `:` seul ne distingue pas une declaration d'un selecteur : `a:hover`
    // en contient un aussi. Une declaration a une partie gauche qui est un nom
    // de propriete et une partie droite non vide qui n'est pas une pseudo.
    const left = condition.slice(0, separator).trim();
    const right = condition.slice(separator + 1).trim();

    // `::` marque un pseudo-element : la partie droite commence alors par `:`.
    if (right.startsWith(':')) return 'css-selector';

    const looksLikeProperty = /^[a-z-]+$/i.test(left) && right !== '';
    const looksLikePseudo = /^(hover|focus|active|visited|first-child|last-child|nth-child|before|after)\b/i.test(
      right
    );

    if (looksLikeProperty && !looksLikePseudo) return 'css-property';
    return 'css-selector';
  }

  return 'text';
};

/** Verifie la presence d'une balise ouvrante, quels que soient ses attributs. */
const matchHtmlElement = (html: string, elementName: string): boolean => {
  if (!elementName) return false;
  // L'echappement est indispensable : une condition mal saisie (`<h1(>`) ferait
  // autrement lever une exception qui remonterait jusqu'au clic sur "Executer".
  return new RegExp(`<${escapeRegExp(elementName)}(\\s[^>]*)?/?>`, 'i').test(html);
};

/** Verifie qu'une declaration `propriete: valeur` figure dans le CSS. */
const matchCssProperty = (css: string, property: string, value: string): boolean => {
  if (!property || !value) return false;
  return new RegExp(`${escapeRegExp(property)}\\s*:\\s*${escapeRegExp(value)}`, 'i').test(css);
};

/**
 * Verifie qu'un selecteur est utilise dans le CSS.
 *
 * La correspondance est bornee a droite pour que `.box` ne soit pas valide par
 * `.boxer` : le caractere suivant doit terminer le selecteur (espace, accolade,
 * virgule, combinateur...).
 */
const matchCssSelector = (css: string, selector: string): boolean => {
  if (!selector) return false;
  return new RegExp(`${escapeRegExp(selector)}(?![\\w-])`, 'i').test(css);
};

/** Evalue une condition isolee et en decrit le resultat. */
const evaluateCondition = (html: string, css: string, condition: string): ConditionResult => {
  const trimmed = condition.trim();
  const kind = classify(trimmed);

  try {
    switch (kind) {
      case 'html-element': {
        // On ne garde que le nom de la balise : `<h1 class="x">` -> `h1`.
        const inner = trimmed.slice(1, -1).trim();
        const elementName = inner.split(/[\s/>]/)[0] ?? '';
        return {
          condition: trimmed,
          kind,
          subject: elementName,
          passed: matchHtmlElement(html, elementName),
        };
      }

      case 'css-property': {
        const separator = trimmed.indexOf(':');
        const property = trimmed.slice(0, separator).trim();
        const value = trimmed.slice(separator + 1).trim().replace(/;$/, '');
        return {
          condition: trimmed,
          kind,
          subject: property,
          expected: value,
          passed: matchCssProperty(css, property, value),
        };
      }

      case 'css-selector': {
        // Les accolades sont facultatives : `a:hover` est un selecteur valide
        // au meme titre que `a:hover { }`.
        const selector = trimmed.split('{')[0].trim();
        return {
          condition: trimmed,
          kind,
          subject: selector,
          passed: matchCssSelector(css, selector),
        };
      }

      default: {
        const needle = trimmed.toLowerCase();
        return {
          condition: trimmed,
          kind,
          subject: trimmed,
          passed: html.toLowerCase().includes(needle) || css.toLowerCase().includes(needle),
        };
      }
    }
  } catch {
    // Une condition impossible a interpreter est consideree comme non
    // satisfaite : elle est signalee a l'eleve sans interrompre l'evaluation
    // des autres conditions.
    return { condition: trimmed, kind, subject: trimmed, passed: false };
  }
};

/**
 * Evalue l'ensemble des conditions d'un exercice.
 *
 * Les conditions vides sont ignorees : l'interface d'administration autorise
 * des lignes blanches qui ne doivent pas bloquer l'eleve.
 */
export const evaluateWinConditions = (
  html: string,
  css: string,
  winConditions: string[] | undefined
): WinEvaluation => {
  const conditions = (winConditions ?? []).filter(condition => condition.trim() !== '');

  if (conditions.length === 0) {
    return { status: 'no-conditions', results: [], failed: [] };
  }

  // Le code commente ne compte pas comme du code ecrit.
  const activeHtml = stripHtmlComments(html ?? '');
  const activeCss = stripCssComments(css ?? '');

  const results = conditions.map(condition => evaluateCondition(activeHtml, activeCss, condition));
  const failed = results.filter(result => !result.passed);

  return {
    status: failed.length === 0 ? 'passed' : 'failed',
    results,
    failed,
  };
};
